import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_PACE_M, SECTORS_MAX, SECTOR_WORD_MAX } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { SETTINGS_PROBLEM_KEY, sectorRows, settingsFormOf, settingsPayload, settingsProblems, useFlagSettings, type SettingsForm } from '../data/pinsUi.ts'
import { fmtNum } from '../lib/format.ts'
import { BackLink, Banner, DisabledHint, Field, NotAllowed, PendingBanner } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'

/** Réglages › Secteurs des drapeaux (M16-C, docs/M16.md "C"): the club's own words for where the flag goes, in the order of the buttons; how many
 *  buttons per row; the club's pace in metres (the device converts a pace typed under "Détails" with it; nothing is stored in paces). One row per site
 *  (`flag_settings.set@1`, flag.manage: an office intent, sent and waited for; 0 creates the row, the revision read rewrites it). Until the Superintendent
 *  saves his words the default grid is shown, marked "(à confirmer)", and nothing is stored. */
export function FlagSettingsPage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const manage = useWriteAccess('flag.manage')
  const row = useFlagSettings(site.site_id)
  const w = useWorkshopAction()
  const [f, setF] = useState<SettingsForm | null>(null)
  // the form opens on the row (or the default words) once the mirror has answered, and follows a row that comes back with a new revision after a save kept on the device
  useEffect(() => { if (row !== undefined && (f === null || w.pending)) { setF(settingsFormOf(row, t)); w.reset() } }, [row === undefined ? -1 : row?.revision ?? 0])   // eslint-disable-line react-hooks/exhaustive-deps
  if (!manage.permitted) return <NotAllowed permission="flag.manage" />
  if (row === undefined || f === null) return <p>{t('common.loading')}</p>
  const problems = settingsProblems(f)
  const words = f.words
  const setWord = (i: number, v: string) => setF({ ...f, words: words.map((x, j) => (j === i ? v : x)) })
  const submit = async () => { await w.run('flag_settings.set@1', settingsPayload(f, row?.revision ?? 0), 'pins.settingsSaved') }
  return (
    <section className="flex flex-col gap-4" data-testid="flags-page" data-default={row ? '0' : '1'}>
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="flag" className="size-7 text-flag" />{t('pins.settingsTitle')}</h1>
      <p className="meta">{t('pins.settingsHelp')}</p>
      {!row && <Banner tone="info" testId="flags-default">{t('pins.settingsDefault')}</Banner>}
      {manage.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      <form className="flex flex-col gap-4" data-testid="flags-form" onSubmit={(e) => { e.preventDefault(); void submit() }}>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t('pins.words')}</legend>
          {words.map((wd, i) => (
            <div key={i} className="flex items-end gap-2">
              <Field label={t('pins.wordN', { n: i + 1 })} htmlFor={`flags-word-${i}`}>
                <input id={`flags-word-${i}`} className="input" maxLength={SECTOR_WORD_MAX} value={wd} onChange={(e) => setWord(i, e.target.value)} data-testid="flags-word" />
              </Field>
              <button type="button" className="btn !px-4" disabled={words.length <= 1} onClick={() => setF({ ...f, words: words.filter((_, j) => j !== i) })} aria-label={`${t('pins.removeWord')} ${i + 1}`} data-testid="flags-remove">{t('pins.removeWord')}</button>
            </div>
          ))}
          <button type="button" className="btn" disabled={words.length >= SECTORS_MAX} onClick={() => setF({ ...f, words: [...words, ''] })} data-testid="flags-add">{t('pins.addWord')}</button>
        </fieldset>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t('pins.columns')}</legend>
          <div className="grid grid-cols-4 gap-2" role="group" aria-label={t('pins.columns')}>
            {[1, 2, 3, 4].map((n) => <button key={n} type="button" className={`btn ${f.columns === n ? 'btn-primary' : ''}`} aria-pressed={f.columns === n} onClick={() => setF({ ...f, columns: n })} data-testid={`flags-columns-${n}`}>{n}</button>)}
          </div>
        </fieldset>
        <Field label={t('pins.pace')} hint={t('pins.paceHint')} htmlFor="flags-pace">
          <input id="flags-pace" className="input" inputMode="decimal" placeholder={fmtNum(DEFAULT_PACE_M, lang, 2)} value={f.pace} onChange={(e) => setF({ ...f, pace: e.target.value })} data-testid="flags-pace" />
        </Field>
        <div className="flex flex-col gap-2" data-testid="flags-preview">
          <h2 className="text-sm font-semibold">{t('pins.preview')}</h2>
          {sectorRows(words.map((x) => x.trim()).filter(Boolean), f.columns).map((r, i) => (
            <div key={i} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${f.columns}, minmax(0, 1fr))` }}>
              {r.map((s, j) => <span key={j} className="flex min-h-14 items-center justify-center rounded-xl border-2 border-slate-300 px-2 text-center text-base font-semibold dark:border-night-line" data-testid="flags-preview-cell">{s}</span>)}
            </div>
          ))}
        </div>
        {w.pending && <PendingBanner />}
        {w.error && <Banner tone="error" testId="flags-error">{w.error}</Banner>}
        {w.ok && <Banner tone="ok" testId="flags-saved">{w.ok}</Banner>}
        <button className="btn btn-primary" disabled={w.busy || w.pending || problems.length > 0 || !manage.allowed} data-testid="flags-save">{t('pins.settingsSave')}</button>
        <DisabledHint show={problems.length > 0} testId="flags-hint">{t(SETTINGS_PROBLEM_KEY[problems[0] ?? 'words'])}</DisabledHint>
      </form>
    </section>
  )
}
